import React, { useEffect, useState, ReactNode } from 'react';
import pluralize from 'pluralize';
import { useLocation } from 'react-router-dom';

import routeTree from '@/routes';

export { default as useBreadcrumbs } from './useBreadcrumbs';
export { default as useUpdateBreadcrumbParam } from './useUpdateBreadcrumbParam';

interface Route {
  key: string;
  name: string;
  path: string;
  routes?: Route[];
}

export interface Breadcrumb {
  key: string;
  label: string;
  path: string;
  isParam: boolean;
}

interface BreadcrumbContextType {
  breadcrumbs: Breadcrumb[];
  setParamLabel: (paramKey: string, label: string) => void;
}

interface BreadcrumbContextProviderProps {
  children: ReactNode;
}

const getBreadcrumbKeys = (
  pathname: string,
  routes: Route[] = [],
  accBreadcrumbs: Breadcrumb[] = [],
): Breadcrumb[] => {
  let result = [...accBreadcrumbs];

  const currRouteKeys = pathname.split('/').filter((a) => a);

  const first = currRouteKeys[0];
  const rest = currRouteKeys.slice(1);
  const restString = rest.join('/');

  const found = routes.find((item) => item.key === first);

  if (found) {
    result = [
      ...result,
      {
        key: found.key,
        label: found.name,
        path: found.path,
        isParam: false,
      },
    ];

    if (found.routes?.length && rest.length) {
      result = [...getBreadcrumbKeys(restString, found.routes, result)];
    }
  } else {
    const parent = result[result.length - 1];

    if (!parent) return result;

    const singKey = pluralize.singular(parent.key);

    const foundParam = routes.find(
      (item) => item.key.indexOf(':') === 0 && item.key.includes(singKey),
    );

    if (foundParam) {
      result = [
        ...result,
        {
          key: foundParam.key,
          label: 'commons.form.loading',
          path: foundParam.path.replace(foundParam.key, first),
          isParam: true,
        },
      ];

      if (foundParam.routes?.length && rest.length) {
        result = [...getBreadcrumbKeys(restString, foundParam.routes, result)];
      }
    }
  }

  return result;
};

export const BreadcrumbContext =
  React.createContext<BreadcrumbContextType | null>(null);

const BreadcrumbContextProvider: React.FC<BreadcrumbContextProviderProps> = ({
  children,
}) => {
  const appRoutes =
    routeTree.find((item: Route) => item.key === 'app')?.routes || [];

  const pathname = useLocation().pathname.replace('/app', '');

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(
    getBreadcrumbKeys(pathname, appRoutes),
  );

  useEffect(() => {
    const bc = getBreadcrumbKeys(pathname, appRoutes).map((a) => {
      if (a.isParam && a.label === 'commons.form.loading') {
        const prevBc = breadcrumbs.find((b) => a.key === b.key);
        return prevBc || a;
      }
      return a;
    });

    const areBreadcrumbsEqual =
      JSON.stringify(bc) === JSON.stringify(breadcrumbs);

    if (!areBreadcrumbsEqual) {
      setBreadcrumbs(bc);
    }
  }, [pathname, appRoutes, breadcrumbs]);

  const setParamLabel = (paramKey: string, label: string) => {
    const found = breadcrumbs.find(
      (item) => item.key === paramKey && item.isParam,
    );

    if (!found) return;

    const bc = breadcrumbs.map((item) => {
      if (item.key === paramKey) {
        return { ...item, label };
      }

      return item;
    });

    setBreadcrumbs(bc);
  };

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setParamLabel }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export default BreadcrumbContextProvider;
